/**
 * A conta da Global na corretora, no cartao do plano (spec 043, secao 4; spec 019, 3.3): o numero da conta, a situacao
 * que importa pro cliente e o saldo sempre com a idade. So traduz o que o backend mandou: estado, saldo e resultado
 * vem prontos. Acende pelo modulo (`nelogica-v2` e `saldo`), nunca pela marca.
 */
import { SaldoNaCorretora, formatarDia, numerosDoSaldo, type DadoDaConta, type Fato, type MotivoSemSaldo, type SituacaoDaConta } from "@mtc/ui";
import type { Matricula, ModuloContaNaCorretora } from "./tipos";

/** Na Global o resumo `corretora` e o do `nelogica-v2` (conta e estado), nao o da Prop (subconta e mesa). */
export const contaDaGlobal = (m: Matricula): ModuloContaNaCorretora | null => (m.modulos.corretora as unknown as ModuloContaNaCorretora | undefined) ?? null;

/**
 * Por que nao ha saldo, na lingua do cliente. Plano encerrado nao mostra saldo nenhum (a conta pode ser de outro plano
 * agora). Sem conta, o bloco nao aparece: a conta ja diz "Em preparacao" e o proximo passo explica (a frase fica pro
 * caso raro de plano liberado sem numero).
 */
export const SEM_SALDO_PRO_CLIENTE: Record<Exclude<MotivoSemSaldo, "PLANO_ENCERRADO">, string> = {
  LEITURA_DESLIGADA: "O saldo ainda não está disponível por aqui.",
  AINDA_NAO_VISTA: "Ainda não recebemos o saldo desta conta da corretora.",
  SEM_CONTA: "A conta ainda está sendo preparada.",
};

/** A etapa da Global (spec 044): Challenge ou Funded, desde quando. So existe com a conta liberada. */
export function etapaDaGlobal(m: Matricula): Fato[] {
  const c = contaDaGlobal(m);
  return c?.etapa_rotulo ? [{ rotulo: "Etapa", valor: c.etapa_rotulo, detalhe: c.etapa_desde ? `desde ${formatarDia(c.etapa_desde)}` : undefined }] : [];
}

/** O numero da conta em destaque, com copiar. Antes da liberacao, o que esta acontecendo. */
export function contaDoPlanoDaGlobal(m: Matricula): DadoDaConta[] {
  const c = contaDaGlobal(m);
  if (c?.conta) return [{ rotulo: "Conta na corretora", valor: c.conta, copiar: true }];
  return [{ rotulo: "Conta na corretora", valor: m.status === "ENCERRADA" ? "Sem conta" : "Em preparação" }];
}

/**
 * A situacao que o cliente le (decisoes 3.11 e 3.12): ativa, bloqueada ou desativada na corretora, e o bloqueio por
 * risco com a frase da corretora. Nao encontrada, sem leitura e leitura desligada sao assunto da equipe: nao assustam o
 * cliente, entao nao aparecem. Nunca "situacao financeira": sem cobranca recorrente, bloqueio nao e falta de pagamento.
 */
export function situacoesProCliente(m: Matricula): SituacaoDaConta[] {
  if (m.status === "ENCERRADA") return [];
  const estado = contaDaGlobal(m)?.estado;
  const s = m.modulos.saldo;
  const risco = s?.bloqueada_por_risco ? s.motivo_do_risco?.trim() : null;
  const lista: SituacaoDaConta[] = [];
  if (estado === "BLOQUEADA") lista.push({ tom: "erro", titulo: "Bloqueada na corretora" });
  if (estado === "DESATIVADA") lista.push({ tom: "erro", titulo: "Desativada na corretora" });
  if (s?.bloqueada_por_risco) lista.push({ tom: "erro", titulo: risco ? `Bloqueada por risco: ${risco}` : "Bloqueada por risco" });
  // "Ativa" so quando nada mais diz o contrario: ativa na API e bloqueada pelo risco ao mesmo tempo confundiria. "Na
  // corretora" porque o selo do cartao ja diz "Ativa" do plano, que e outra coisa.
  if (estado === "ATIVA" && !lista.length) lista.push({ tom: "sucesso", titulo: "Ativa na corretora" });
  return lista;
}

/** O bloco da conta no cartao do plano da Global: situacao, saldo com a idade, saldo inicial e resultado. */
export function ContaNaCorretoraDoCliente({ m }: { m: Matricula }) {
  const situacoes = situacoesProCliente(m);
  const s = m.modulos.saldo;
  if (m.status === "ENCERRADA" || !s || s.motivo_sem_saldo === "PLANO_ENCERRADO" || (s.motivo_sem_saldo === "SEM_CONTA" && m.status === "AGUARDANDO_INICIO")) return situacoes.length ? <SaldoNaCorretora situacoes={situacoes} /> : null;
  const { saldo, inicial, resultado } = numerosDoSaldo(s);
  return (
    <SaldoNaCorretora
      situacoes={situacoes}
      saldo={saldo}
      semSaldo={s.motivo_sem_saldo ? SEM_SALDO_PRO_CLIENTE[s.motivo_sem_saldo] : undefined}
      // Sem saldo inicial no plano, o cliente le so "Sem base pra calcular" no resultado: "nao informado" nao ajuda ele.
      inicial={inicial ?? undefined}
      resultado={resultado}
    />
  );
}
